import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Canvas, useThree } from '@react-three/fiber'
import { useAppStore } from '../stores/appStore'
import { setLanguage } from '../i18n'
import { buildApiUrl } from '../config'
import Scene from './three/Scene'
import { getTabCameraPosition } from './three/cameraPositions'

/**
 * Minimal 3D scene for the /screenshot entrypoint only.
 * Reuses the same store, config wiring, and Scene as BedVisualizer
 * but renders nothing else: no TopLinks, Drawer, SizeSelector, ZoomButtons, etc.
 */
export default function ScreenshotVisualizer() {
  const {
    bed,
    market,
    lang,
    loadBedVariation,
    bedConfig,
    getBedVariation,
    textureResolution,
    isLoadingBedVariation,
    isLoadingModels,
  } = useAppStore()
  const { selectedSize, currentModel } = bedConfig.bed
  const bedRenderReadyFlag = (window as any).__bed_render_ready__ === true
  const webglContextLost = (window as any).__webgl_context_lost__ === true

  const { data } = useQuery({
    queryKey: ['bed', bed, market, lang],
    queryFn: () =>
      fetch(buildApiUrl(`bed-variations/${bed}/?market=${market}&lang=${lang}`)).then((res) => res.json()),
    enabled: !!bed,
  })

  useEffect(() => {
    if (bed && data?.slug) {
      loadBedVariation(data.slug)
    }
  }, [bed, data?.slug, loadBedVariation])

  useEffect(() => {
    if (lang) {
      setLanguage(lang)
    }
  }, [lang])

  // Basic debug info to understand what state the screenshot view is in.
  useEffect(() => {
    const bedVariation = getBedVariation()
    const info = {
      bed,
      market,
      lang,
      selectedSize,
      bedVariationSlug: bedVariation?.slug,
      hasCurrentModel: !!currentModel,
      textileId: bedConfig.textile?.id ?? null,
      legsId: bedConfig.legs?.id ?? null,
      headboardId: bedConfig.headboard?.id ?? null,
      topperId: bedConfig.topper?.id ?? null,
      bedsideTableId: bedConfig.bedsideTable?.id ?? null,
      ottomanId: bedConfig.ottoman?.id ?? null,
      accessoriesCount: bedConfig.accessories.length,
      textureResolution,
      isLoadingBedVariation,
      isLoadingModels,
      bedRenderReady: (window as any).__bed_render_ready__ ?? null,
    }
    ;(window as any).__screenshot_debug__ = info
    console.log('[ScreenshotVisualizer] debug state', info)
  }, [
    bed,
    market,
    lang,
    selectedSize,
    currentModel,
    bedConfig.textile,
    bedConfig.legs,
    bedConfig.headboard,
    bedConfig.topper,
    bedConfig.bedsideTable,
    bedConfig.ottoman,
    bedConfig.accessories,
    textureResolution,
    isLoadingBedVariation,
    isLoadingModels,
    getBedVariation,
  ])

  // Fixed desktop-style camera for screenshot; allow URL-driven presets via ?camera=
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null
  const camKey = url?.searchParams.get('camera') || url?.searchParams.get('cam') || null
  const preset = camKey ? getTabCameraPosition(camKey, false) : undefined
  const cameraPosition = (preset?.position || [0, 15, 25]) as [number, number, number]

  // Transparent clear color so PNGs have alpha (no magenta debug background)
  function DebugBackground() {
    const { gl } = useThree()
    useEffect(() => {
      gl.setClearColor(0x000000, 0)
    }, [gl])
    return null
  }

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {(!bed || !selectedSize) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              color: '#000',
              fontSize: 16,
              zIndex: 5,
            }}
          >
            {!bed ? 'No bed slug resolved from URL' : 'No selectedSize in config / store'}
          </div>
        )}
        <Canvas
          shadows="variance"
          camera={{ position: cameraPosition, fov: 22 }}
          gl={{ preserveDrawingBuffer: true, alpha: true }}
        >
          <DebugBackground />
          <Scene />
        </Canvas>
      </div>
    </div>
  )
}
